import type { FC } from "react";

import { Chip } from "@heroui/react";

import { availableLanguages } from "@/i18n";

interface LanguageBadgeProps {
  sourceLanguage?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
  dataId?: string;
}

export const LanguageBadge: FC<LanguageBadgeProps> = ({
  sourceLanguage,
  size = "sm",
  className = "",
  dataId = "language-badge",
}) => {
  const getShortLanguage = (code?: string | null): string => {
    if (!code) return "??";

    // Special case for English - show EN instead of GB
    if (code === "en-GB") return "EN";

    // Handle simple language codes (e.g., "en", "de")
    if (code.length === 2) return code.toUpperCase();

    // Handle region-specific codes (e.g., "en-GB", "de-DE")
    const parts = code.split("-");

    if (parts.length > 1) {
      // For consistency, show the country code (e.g., US, DE)
      return parts[1].toUpperCase();
    }

    // Fallback to uppercase of first two characters
    return code.substring(0, 2).toUpperCase();
  };

  const getLanguageName = (code?: string | null): string => {
    if (!code) return "Unknown Language";

    const lang = availableLanguages.find((l) => l.code === code);

    return lang?.nativeName || code;
  };

  const shortCode = getShortLanguage(sourceLanguage);
  const fullName = getLanguageName(sourceLanguage);

  return (
    <Chip
      aria-label={`Language: ${fullName}`}
      className={`uppercase font-semibold ${className}`}
      data-id={dataId}
      size={size}
      title={fullName}
      variant="flat"
    >
      {shortCode}
    </Chip>
  );
};
